/**
 * Capitalize the first letter of a string.
 * @example capitalize("hello world") // "Hello world"
 */
export function capitalize(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Convert a string to camelCase.
 * @example toCamelCase("hello-world_foo") // "helloWorldFoo"
 */
export function toCamelCase(str: string): string {
  return str
    .replace(/[-_\s]+(.)/g, (_, char: string) => char.toUpperCase())
    .replace(/^(.)/, (c) => c.toLowerCase());
}

/**
 * Convert a string to kebab-case.
 * @example toKebabCase("helloWorldFoo") // "hello-world-foo"
 */
export function toKebabCase(str: string): string {
  return str
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .replace(/[\s_]+/g, "-")
    .toLowerCase();
}

/**
 * Convert a string to snake_case.
 * @example toSnakeCase("helloWorldFoo") // "hello_world_foo"
 */
export function toSnakeCase(str: string): string {
  return str
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .toLowerCase();
}

/**
 * Truncate a string to a max length, appending an ellipsis if needed.
 * @example truncate("Hello, world!", 8) // "Hello..."
 */
export function truncate(str: string, maxLength: number, ellipsis = "..."): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - ellipsis.length) + ellipsis;
}

/**
 * Slugify a string for use in URLs.
 * @example slugify("Hello World! Foo") // "hello-world-foo"
 */
export function slugify(str: string): string {
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/[\s-]+/g, "-");
}

/**
 * Count occurrences of a substring within a string.
 * @example countOccurrences("banana", "an") // 2
 */
export function countOccurrences(str: string, sub: string): number {
  if (!sub) return 0;
  let count = 0;
  let pos = str.indexOf(sub);
  while (pos !== -1) {
    count++;
    pos = str.indexOf(sub, pos + sub.length);
  }
  return count;
}

/**
 * Pad a string on both sides to center it within a given width.
 * @example centerPad("hi", 8) // "   hi   "
 */
export function centerPad(str: string, width: number, char = " "): string {
  const total = width - str.length;
  if (total <= 0) return str;
  const left = Math.floor(total / 2);
  const right = total - left;
  return char.repeat(left) + str + char.repeat(right);
}

/**
 * Check if a string is a valid email address.
 */
export function isEmail(str: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str);
}

/**
 * Remove all whitespace from a string.
 */
export function stripWhitespace(str: string): string {
  return str.replace(/\s+/g, "");
}

/**
 * Reverse a string.
 * @example reverseString("hello") // "olleh"
 */
export function reverseString(str: string): string {
  return [...str].reverse().join("");
}
